import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { resetRequestState } from "../../utils/resetRequestState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { match } from "./utils";
import { calculateAdvancement } from "../../utils/calculateAdvancement";

const initialState: State = [];

function handleRequest(
  state: State,
  { projects }: Boundaries["retrieveWbsMeta"]["request"],
): State {
  const index = state.findIndex((req) => match(req.projects, projects));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [...state, { request: setRequestStateToPending(), projects, meta: {} }];
}

function handleSuccess(
  state: State,
  { projects, meta }: Boundaries["retrieveWbsMeta"]["success"],
): State {
  const index = state.findIndex((req) => match(req.projects, projects));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), meta },
        ...state.slice(index + 1),
      ]
    : [...state, { request: setRequestStateToSuccess(), projects, meta }];
}

function handleFailure(
  state: State,
  { projects }: Boundaries["retrieveWbsMeta"]["failure"],
): State {
  const index = state.findIndex((req) => match(req.projects, projects));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [...state, { request: setRequestStateToFailure(), projects, meta: {} }];
}

function handleMarkTaskAsCompleted(
  state: State,
  { task }: Boundaries["markTaskAsCompleted"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    meta: !!req.meta[task.uuid]
      ? {
          ...req.meta,
          [task.uuid]: {
            ...req.meta[task.uuid],
            percentComplete: 100,
          },
        }
      : req.meta,
  }));
}

function handleMarkMilestoneAsCompleted(
  state: State,
  { milestone }: Boundaries["markMilestoneAsCompleted"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    meta: !!req.meta[milestone.uuid]
      ? {
          ...req.meta,
          [milestone.uuid]: {
            ...req.meta[milestone.uuid],
            percentComplete: 100,
          },
        }
      : req.meta,
  }));
}

function handleEditTaskSucceeded(
  state: State,
  task: Boundaries["editTask"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    meta: !!req.meta[task.uuid]
      ? {
          ...req.meta,
          [task.uuid]: {
            ...req.meta[task.uuid],
            percentComplete: calculateAdvancement(task).percentComplete,
          },
        }
      : req.meta,
  }));
}

// Allocated hours roll up to the task's lists and project: keep the current
// meta displayed, but mark it as not loaded so it gets fetched again (on
// mount, or right away by a mounted WBS page).
function handleEditAllocationSucceeded(state: State): State {
  return state.map((req) => ({ ...req, request: resetRequestState() }));
}

// Spent hours roll up the same way: only the meta showing the time record's
// task, or the validated project, is marked as not loaded.
function handleSaveTimeRecordSucceeded(
  state: State,
  { timeRecord }: Boundaries["saveTimeRecord"]["success"],
): State {
  return state.map((req) =>
    !!req.meta[timeRecord.task.uuid]
      ? { ...req, request: resetRequestState() }
      : req,
  );
}

function handleDeleteTimeRecordSucceeded(
  state: State,
  { timeRecord }: Boundaries["deleteTimeRecord"]["success"],
): State {
  return state.map((req) =>
    !!req.meta[timeRecord.task.uuid]
      ? { ...req, request: resetRequestState() }
      : req,
  );
}

function handleValidateTimeRecordsUpToDateSucceeded(
  state: State,
  { project }: Boundaries["validateTimeRecordsUpToDate"]["success"],
): State {
  return state.map((req) =>
    req.projects.some(({ uuid }) => uuid === project.uuid)
      ? { ...req, request: resetRequestState() }
      : req,
  );
}

function handleEditMilestoneSucceeded(
  state: State,
  { milestone }: Boundaries["editMilestone"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    meta: !!req.meta[milestone.uuid]
      ? {
          ...req.meta,
          [milestone.uuid]: {
            ...req.meta[milestone.uuid],
            percentComplete: milestone.state === "DONE" ? 100 : 0,
          },
        }
      : req.meta,
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveWbsMetaRequested]: handleRequest,
    [events.retrieveWbsMetaSucceeded]: handleSuccess,
    [events.retrieveWbsMetaFailed]: handleFailure,
    [events.markTaskAsCompletedSucceeded]: handleMarkTaskAsCompleted,
    [events.markMilestoneAsCompletedSucceeded]: handleMarkMilestoneAsCompleted,
    [events.editTaskSucceeded]: handleEditTaskSucceeded,
    [events.editMilestoneSucceeded]: handleEditMilestoneSucceeded,
    [events.editAllocationSucceeded]: handleEditAllocationSucceeded,
    [events.saveTimeRecordSucceeded]: handleSaveTimeRecordSucceeded,
    [events.deleteTimeRecordSucceeded]: handleDeleteTimeRecordSucceeded,
    [events.validateTimeRecordsUpToDateSucceeded]:
      handleValidateTimeRecordsUpToDateSucceeded,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
